import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { getJournalEmails } from 'redux/selectors';
import * as JournalEmailActions from 'redux/actions/journalEmail';
import { updateUI } from 'redux/actions/ui';
import iconCopy from 'assets/images/icons_svg/copy_mission_email.svg';

const EmailToJournalDialog = ({ dispatch, journal, journalEmails, close }) => {
    const [copied, setCopied] = useState(false);

    // If the store already has emails cached, no need to show a loader
    const [isLoading, setIsLoading] = useState(
        !journalEmails || journalEmails.length === 0
    );
    const hasFetched = useRef(false);

    useEffect(() => {
        if (hasFetched.current) {
            return;
        }
        hasFetched.current = true;
        dispatch(JournalEmailActions.fetch_journal_emails());
    }, [dispatch]);

    // Stop the spinner once the store actually has data
    useEffect(() => {
        if (isLoading && journalEmails && journalEmails.length > 0) {
            setIsLoading(false);
        }
    }, [journalEmails, isLoading]);

    // Safety fallback: stop spinner after 6s regardless
    useEffect(() => {
        if (!isLoading) {
            return;
        }
        const t = setTimeout(() => setIsLoading(false), 6000);
        return () => clearTimeout(t);
    }, [isLoading]);

    const _close = useCallback(() => {
        if (close) {
            close();
        } else {
            dispatch(updateUI({ dialogs: [] }));
        }
    }, [close, dispatch]);

    // Find the email address associated with this specific journal
    const journalEmail = journalEmails?.find(
        (e) => e.journal?.id === journal?.id
    );
    const emailAddress = journalEmail?.address || '';

    const handleCopy = useCallback(() => {
        if (!emailAddress) {
            return;
        }
        navigator.clipboard.writeText(emailAddress);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [emailAddress]);

    return (
        <div
            id="email-to-journal-modal"
            className="modal-frame centered-modal-frame"
        >
            <div className="modal-box modal-box--600">
                <div className="modal__header">Email to Journal</div>
                <div className="modal__body">
                    <div className="form-group form-group--single">
                        <div className="form-group__item">
                            <label
                                htmlFor="journal-email-address"
                                style={{ fontWeight: 600 }}
                            >
                                Journal Email Address
                            </label>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    padding: '0 12px',
                                    background: '#fff',
                                    marginTop: '6px',
                                    minHeight: '42px'
                                }}
                            >
                                {isLoading ? (
                                    <div
                                        style={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '10px 0',
                                            color: '#9ca3af',
                                            fontSize: '14px'
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: '14px',
                                                height: '14px',
                                                border: '2px solid #d1d5db',
                                                borderTopColor: '#6b7280',
                                                borderRadius: '50%',
                                                display: 'inline-block',
                                                animation:
                                                    'etj-spin 0.7s linear infinite'
                                            }}
                                        />
                                        <style>{`
                                            @keyframes etj-spin {
                                                to { transform: rotate(360deg); }
                                            }
                                        `}</style>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            id="journal-email-address"
                                            type="text"
                                            readOnly
                                            value={emailAddress}
                                            placeholder={
                                                !emailAddress
                                                    ? 'We are retrieving your email address, Please wait for sometime!'
                                                    : ''
                                            }
                                            style={{
                                                flex: 1,
                                                border: 'none',
                                                outline: 'none',
                                                padding: '10px 0',
                                                fontSize: '14px',
                                                background: 'transparent',
                                                color: '#000000'
                                            }}
                                        />
                                        <button
                                            onClick={handleCopy}
                                            disabled={!emailAddress}
                                            title={
                                                copied
                                                    ? 'Copied!'
                                                    : 'Copy to clipboard'
                                            }
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: emailAddress
                                                    ? 'pointer'
                                                    : 'default',
                                                padding: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                opacity: emailAddress ? 1 : 0.4,
                                                position: 'relative'
                                            }}
                                            aria-label="Copy email address"
                                        >
                                            {copied ? (
                                                <span
                                                    style={{
                                                        fontSize: '12px',
                                                        color: '#16a34a',
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    Copied!
                                                </span>
                                            ) : (
                                                <img
                                                    src={iconCopy}
                                                    alt="Copy"
                                                    style={{
                                                        width: '18px',
                                                        height: '18px'
                                                    }}
                                                />
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <p
                        style={{
                            fontSize: '14px',
                            color: '#000000',
                            marginTop: '16px',
                            lineHeight: '1.6'
                        }}
                    >
                        This email address is linked to your Mission JRNL. Any
                        email sent to this address will be automatically
                        imported into your journal. Save it to your contacts so
                        it&apos;s easy to use. You can email it directly or CC
                        it when sending messages to a group.
                    </p>
                </div>
                <div
                    className="modal__footer"
                    style={{ justifyContent: 'flex-end' }}
                >
                    <button
                        className="btn btn-outline"
                        onClick={_close}
                        style={{ borderRadius: '20px', padding: '8px 24px' }}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const state = createStructuredSelector({
    journalEmails: getJournalEmails
});

export default connect(state)(EmailToJournalDialog);
